import { router } from '@inertiajs/react'
import { Input } from '@/components/ui/input'
import { useState, useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'

interface InlineEditFieldProps {
  transactionId: number
  field: string
  value: string
  displayValue?: string | React.ReactNode
  type?: 'text' | 'date' | 'number'
  className?: string
  placeholder?: string
}

export function InlineEditField({
  transactionId,
  field,
  value,
  displayValue,
  type = 'text',
  className = '',
  placeholder,
}: InlineEditFieldProps) {
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  useEffect(() => {
    setEditValue(value)
  }, [value])

  function submit() {
    setEditing(false)
    if (editValue !== value) {
      router.patch(`/transactions/${transactionId}`, {
        transaction: { id: transactionId, [field]: editValue },
      }, { preserveScroll: true })
    }
  }

  if (editing) {
    return (
      <Input
        ref={inputRef}
        type={type}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={submit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit()
          if (e.key === 'Escape') {
            setEditValue(value)
            setEditing(false)
          }
        }}
        className={cn('h-7 text-[13px]', className)}
      />
    )
  }

  const isEmpty = !value && !displayValue

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={cn(
        'flex items-center rounded px-1.5 py-0.5 -mx-1.5 text-left transition-colors hover:bg-muted',
        className
      )}
    >
      {isEmpty ? (
        <span className="text-muted-foreground/50 italic text-[13px]">
          {placeholder || 'Click to edit'}
        </span>
      ) : (
        displayValue || value
      )}
    </button>
  )
}
